// Shapes of the lakes smaller than a hex (public/data/small-lakes.json), for the water decals.
//
// The file gives a point and an area, nothing about the outline. The outline here is a smooth
// closed curve grown from the lake's name (so it is the same in every session), scaled so that
// its area is exactly area_km2. It is not the real shoreline and must not be read as one.
//
//        r(θ) = r0 · s(θ),   s(θ) = 1 + Σ a_k cos(kθ + φ_k)    (θ = angle on the ground, in km)
//
//   area = ½ ∫ r² dθ = π r0² (1 + ½ Σ a_k²)     → r0 = √(A / (π (1 + ½ Σ a_k²)))
//
// The outline lives in km, not in map units: the Patterson map stretches ground by latitude, and a
// lake drawn round in map units would be wrong on the ground. Map units per km come from the same
// local scale the bake uses for areas (tools/bake/worldciv_bake/borders_sample.py).

import { K, lonLatToWorld, pattersonY } from '../core/grid.ts';

/** A lake without a hex: where it is, how big, and what says so. */
export interface SmallLake {
  name: string;
  lat: number;
  lon: number;
  area_km2: number;
  sources: readonly string[];
}

/** One harmonic of the outline. */
export interface Harmonic {
  k: number;
  amp: number;
  phase: number;
}

/** Everything the shader needs about one lake. */
export interface LakeShape {
  /** Map position of the lake's point. */
  x: number;
  z: number;
  /** Kilometres of ground per map unit, along x and along z, at the lake's latitude. */
  kmPerX: number;
  kmPerZ: number;
  /** Mean outline radius (km). */
  radiusKm: number;
  harmonics: readonly Harmonic[];
  /** Farthest the outline reaches from the point, in map units (a cheap early-out for the shader). */
  reachUnits: number;
}

const EARTH_RADIUS_KM = 6371.0072; // authalic, as in the bake
const DEG = Math.PI / 180;
const DERIV_STEP = 1e-4;
/** More lakes than this are ignored: each one adds shader code. */
const MAX_LAKES = 32;

// Outline: six harmonics, amplitude falling as k^-AMP_FALL, each jittered. The sum of amplitudes
// stays under AMP_SUM_MAX, so s(θ) stays above 1 - AMP_SUM_MAX and the outline is star-shaped
// (no islands, no pinched waist).
const HARMONICS = 6;
const AMP_FIRST = 0.24;
const AMP_FALL = 1.1;
const AMP_JITTER = [0.6, 1] as const;
const AMP_SUM_MAX = 0.6;

// FNV-1a and mulberry32: a tiny seeded stream, so the outline depends on the name alone.
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const MULBERRY_STEP = 0x6d2b79f5;
const UINT32 = 4294967296;

function seedOf(name: string): number {
  let h = FNV_OFFSET;
  for (let i = 0; i < name.length; i++) {
    h = Math.imul(h ^ name.charCodeAt(i), FNV_PRIME);
  }
  return h >>> 0;
}

function stream(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + MULBERRY_STEP) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / UINT32;
  };
}

/** Ground km per map unit along x and z at a latitude (degrees). */
export function kmPerUnit(lat: number): { x: number; z: number } {
  const phi = lat * DEG;
  const dy = (pattersonY(phi + DERIV_STEP) - pattersonY(phi - DERIV_STEP)) / (2 * DERIV_STEP);
  return { x: (EARTH_RADIUS_KM * Math.cos(phi)) / K, z: EARTH_RADIUS_KM / (K * dy) };
}

function harmonicsOf(name: string): Harmonic[] {
  const next = stream(seedOf(name));
  const out: Harmonic[] = [];
  for (let k = 1; k <= HARMONICS; k++) {
    const jitter = AMP_JITTER[0] + (AMP_JITTER[1] - AMP_JITTER[0]) * next();
    out.push({ k, amp: AMP_FIRST * jitter * k ** -AMP_FALL, phase: next() * 2 * Math.PI });
  }

  // Scale down when the amplitudes could add up to a pinched outline.
  const sum = out.reduce((s, h) => s + h.amp, 0);
  const fit = sum > AMP_SUM_MAX ? AMP_SUM_MAX / sum : 1;
  return out.map((h) => ({ ...h, amp: h.amp * fit }));
}

/** s(θ): the outline's radius relative to its mean. */
export function relativeRadius(harmonics: readonly Harmonic[], angle: number): number {
  let s = 1;
  for (const h of harmonics) {
    s += h.amp * Math.cos(h.k * angle + h.phase);
  }
  return s;
}

/** The shape of one lake: outline harmonics, and the scale that gives it exactly area_km2. */
export function lakeShape(lake: SmallLake): LakeShape {
  const at = lonLatToWorld(lake.lon, lake.lat);
  const scale = kmPerUnit(lake.lat);
  const harmonics = harmonicsOf(lake.name);
  const power = harmonics.reduce((s, h) => s + h.amp * h.amp, 0) / 2;
  const radiusKm = Math.sqrt(lake.area_km2 / (Math.PI * (1 + power)));
  const reachKm = radiusKm * (1 + harmonics.reduce((s, h) => s + h.amp, 0));

  return {
    x: at.x,
    z: at.z,
    kmPerX: scale.x,
    kmPerZ: scale.z,
    radiusKm,
    harmonics,
    reachUnits: reachKm / Math.min(scale.x, scale.z),
  };
}

/** The shapes of the lakes that get drawn: the first MAX_LAKES. */
export function lakeShapes(lakes: readonly SmallLake[]): LakeShape[] {
  return lakes.slice(0, MAX_LAKES).map(lakeShape);
}
